import React, { useState, useEffect } from 'react';
import { Camera, BarChart3, CheckSquare, FileText, Shield, Settings, Menu, X, ChevronRight } from 'lucide-react';

// ============================================================================
// MODELS (Data Layer)
// ============================================================================

const API_BASE = '/api'; // Cloudflare Workers endpoint

class BaseModel {
  static async fetch(endpoint) {
    const response = await fetch(`${API_BASE}${endpoint}`);
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }

  static async post(endpoint, data) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }
}

class DashboardModel extends BaseModel {
  static async getStats() {
    // In production, this would call your Workers API
    return {
      stats: [
        { label: 'Active Tasks', value: '24', change: '+12%', color: '#667eea' },
        { label: 'Completed', value: '156', change: '+8%', color: '#28a745' },
        { label: 'In Progress', value: '18', change: '+3%', color: '#ffc107' },
        { label: 'Pending Review', value: '6', change: '-2%', color: '#dc3545' }
      ],
      activities: [
        { action: 'Task completed', item: 'System Integration Module', time: '2 hours ago', user: 'Sarah Chen' },
        { action: 'New task created', item: 'Database Optimization', time: '4 hours ago', user: 'Mike Johnson' },
        { action: 'Report generated', item: 'Q4 Performance Report', time: '5 hours ago', user: 'System' },
        { action: 'Module updated', item: 'User Management', time: '1 day ago', user: 'Admin' }
      ],
      quickModules: [
        { name: 'QuickTrack', icon: '⚡', count: '342 tickets', id: 'quicktrack' },
        { name: 'PlanTrack', icon: '📋', count: '156 projects', id: 'plantrack' },
        { name: 'TimeBoard', icon: '⏱️', count: '892 entries', id: 'timeboard' },
        { name: 'LeaveBoard', icon: '🏖️', count: '23 pending', id: 'leaveboard' }
      ]
    };
  }
}

class ModulesModel extends BaseModel {
  static async getModules() {
    return [
      {
        id: 'quicktrack',
        name: 'QuickTrack',
        icon: '⚡',
        color: '#667eea',
        description: 'Rapid issue tracking and ticket management for quick turnaround items.',
        status: 'Active',
        records: 342
      },
      {
        id: 'plantrack',
        name: 'PlanTrack',
        icon: '📋',
        color: '#28a745',
        description: 'Project planning, sprint management, and milestone tracking system.',
        status: 'Active',
        records: 156
      },
      {
        id: 'devops',
        name: 'DevOps',
        icon: '🔧',
        color: '#0366d6',
        description: 'CI/CD pipeline management, deployment tracking, and infrastructure monitoring.',
        status: 'Active',
        records: 89
      },
      {
        id: 'dataops',
        name: 'DataOps',
        icon: '📊',
        color: '#6f42c1',
        description: 'Data pipeline orchestration, ETL workflows, and Power BI integration.',
        status: 'Active',
        records: 234
      },
      {
        id: 'quality',
        name: 'Quality',
        icon: '✓',
        color: '#20c997',
        description: 'Quality assurance, test case management, and defect tracking.',
        status: 'Active',
        records: 523
      },
      {
        id: 'learntrack',
        name: 'LearnTrack',
        icon: '🎓',
        color: '#fd7e14',
        description: 'Learning management, training courses, and skill development tracking.',
        status: 'Active',
        records: 178
      },
      {
        id: 'timeboard',
        name: 'TimeBoard',
        icon: '⏱️',
        color: '#17a2b8',
        description: 'Time tracking, timesheet management, and work hour analytics.',
        status: 'Active',
        records: 892
      },
      {
        id: 'leaveboard',
        name: 'LeaveBoard',
        icon: '🏖️',
        color: '#e83e8c',
        description: 'Leave management, vacation tracking, and absence calendar.',
        status: 'Active',
        records: 445
      }
    ];
  }

  static async getDevOpsPipeline() {
    return {
      capacity: { total: 160, committed: 156, completed: 46, utilization: 97.5 },
      workload: [
        { name: 'John Developer', assigned: 76, capacity: 40, percentage: 190 },
        { name: 'Sarah Frontend', assigned: 20, capacity: 40, percentage: 50 },
        { name: 'Mike Fullstack', assigned: 16, capacity: 40, percentage: 40 },
        { name: 'Jim Manager', assigned: 0, capacity: 40, percentage: 0 }
      ],
      pipeline: {
        backlog: [
          { id: 'PR3-2024-001', title: 'Settlement Vault API Modernization', type: 'PROJECT', priority: 'P1', assignee: 'John Developer', estimate: '80h', storyPoints: 13 }
        ],
        ready: [
          { id: 'BUG-2024-007', title: 'Fix calendar sync issues', type: 'BUG', priority: 'P1', assignee: 'Mike Fullstack', estimate: '16h', storyPoints: 5 }
        ],
        inProgress: [
          { id: 'PR3-2024-002', title: 'Build Negotiator Worklist Dashboard', type: 'PROJECT', priority: 'P2', assignee: 'John Developer', estimate: '60h', storyPoints: 8, progress: 50 }
        ],
        review: [
          { id: 'ADH-2024-015', title: 'Azure DevOps API integration', type: 'ADHOC', priority: 'P3', assignee: 'Sarah Frontend', estimate: '12h', storyPoints: 5 }
        ],
        done: []
      }
    };
  }
}

// ============================================================================
// VIEWS (Presentation Components)
// ============================================================================

const Header = ({ user, onMenuClick }) => (
  <div className="bg-blue-600 text-white shadow-md">
    <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <button onClick={onMenuClick} className="lg:hidden">
          <Menu size={24} />
        </button>
        <div className="text-2xl font-bold tracking-wide">🛰️ ORBIT</div>
      </div>
      <div className="flex items-center gap-3">
        <span className="hidden sm:inline">{user.name}</span>
        <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center font-bold">
          {user.name.charAt(0)}
        </div>
      </div>
    </div>
  </div>
);

const Navigation = ({ currentPage, onNavigate, isOpen, onClose }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'modules', label: 'Modules', icon: Camera },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'audit', label: 'Audit', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      {/* Navigation */}
      <nav className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-auto
        bg-white border-b border-gray-200 shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <span className="font-semibold">Menu</span>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="lg:max-w-7xl lg:mx-auto lg:px-4 lg:flex lg:gap-0">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  onClose();
                }}
                className={`
                  w-full lg:w-auto flex items-center gap-3 px-6 py-4 lg:border-b-3
                  transition-colors
                  ${currentPage === item.id 
                    ? 'text-blue-600 bg-gray-50 lg:border-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600 lg:border-transparent'
                  }
                `}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

const DashboardView = ({ data }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
      <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {data.stats.map((stat, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-gray-600 text-sm mb-2">{stat.label}</div>
              <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-green-600 text-sm mt-1">{stat.change} from last week</div>
            </div>
            <div className="w-16 h-16 rounded-xl" style={{ background: `${stat.color}20` }} />
          </div>
        </div>
      ))}
    </div>

    <div className="grid lg:grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Item</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">User</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.activities.map((activity, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {activity.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{activity.item}</td>
                  <td className="px-6 py-4 text-gray-600">{activity.user}</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{activity.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Access */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Quick Access</h2>
        </div>
        <div className="p-4 space-y-2">
          {data.quickModules.map((module, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer">
              <div className="text-3xl">{module.icon}</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{module.name}</div>
                <div className="text-sm text-gray-600">{module.count}</div>
              </div>
              <ChevronRight className="text-gray-400" size={20} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ModulesView = ({ modules }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">ORBIT Modules</h1>
      <p className="text-gray-600 mt-1">Core API modules and shared services for the ORBIT tracking system.</p>
    </div>

    <div className="mb-4">
      <h2 className="text-xl font-semibold text-gray-900 pb-3 border-b-2 border-gray-200">
        Core API Modules
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {modules.map(module => (
        <div key={module.id} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-xl hover:border-blue-500 transition-all cursor-pointer">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl text-2xl mb-4" 
               style={{ background: `${module.color}20`, color: module.color }}>
            {module.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{module.name}</h3>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{module.description}</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {module.status}
            </span>
            <span className="text-gray-600 text-sm">{module.records.toLocaleString()} records</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DevOpsPipelineView = ({ data }) => {
  const getTypeBadge = (type) => {
    const badges = {
      PROJECT: { color: '#6f42c1', bg: '#f3e8ff' },
      BUG: { color: '#dc3545', bg: '#ffe5e9' },
      ADHOC: { color: '#0366d6', bg: '#d4e4ff' }
    };
    return badges[type] || { color: '#586069', bg: '#f6f8fa' };
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      P1: { color: '#dc3545', bg: '#ffe5e9' },
      P2: { color: '#ffc107', bg: '#fff5cc' },
      P3: { color: '#28a745', bg: '#dcffe4' }
    };
    return badges[priority] || { color: '#586069', bg: '#f6f8fa' };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">DevOps Pipeline Dashboard</h1>
        <p className="text-gray-600 mt-1">Request tracking & capacity planning</p>
      </div>

      {/* Capacity Overview */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Capacity Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Total Capacity</div>
            <div className="text-4xl font-bold text-blue-700">{data.capacity.total}h</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Committed</div>
            <div className="text-4xl font-bold text-yellow-700">{data.capacity.committed}h</div>
            <div className="text-sm text-gray-600 mt-1">{data.capacity.utilization}% utilized</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="text-sm text-gray-600 mb-2">Completed</div>
            <div className="text-4xl font-bold text-green-700">{data.capacity.completed}h</div>
          </div>
        </div>
      </div>

      {/* Team Workload */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Team Workload</h2>
        <div className="space-y-4">
          {data.workload.map((member, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-36 text-sm text-gray-700">{member.name}</div>
              <div className="flex-1 h-8 bg-gray-200 rounded-lg overflow-hidden">
                <div 
                  className={`h-full flex items-center justify-center text-white text-sm font-semibold ${member.percentage > 100 ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(member.percentage, 100)}%` }}
                >
                  {member.percentage}%
                </div>
              </div>
              <div className="w-24 text-sm text-gray-600 text-right">
                {member.assigned}h / {member.capacity}h
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sprint Pipeline */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Sprint Pipeline</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(data.pipeline).map(([status, items]) => (
            <div key={status} className="bg-white rounded-lg shadow border border-gray-200 min-h-96">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="font-semibold text-gray-900 capitalize">{status.replace(/([A-Z])/g, ' $1').trim()}</div>
                <div className="text-sm text-gray-600">{items.length} items</div>
              </div>
              <div className="p-3 space-y-3">
                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-3xl mb-2">🔭</div>
                    <div className="text-sm">No items</div>
                  </div>
                ) : (
                  items.map((item, i) => {
                    const typeBadge = getTypeBadge(item.type);
                    const priorityBadge = getPriorityBadge(item.priority);
                    return (
                      <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="text-xs font-semibold text-gray-600">{item.id}</span>
                          <span className="px-2 py-0.5 rounded text-xs font-semibold" 
                                style={{ background: typeBadge.bg, color: typeBadge.color }}>
                            {item.type}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-semibold ml-auto" 
                                style={{ background: priorityBadge.bg, color: priorityBadge.color }}>
                            {item.priority}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 mb-2 leading-tight">
                          {item.title}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                          <span>👤 {item.assignee}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-600 mt-2">
                          <span>⏱️ {item.estimate}</span>
                          <span>📊 {item.storyPoints} pts</span>
                        </div>
                        {item.progress !== undefined && (
                          <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${item.progress}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlaceholderView = ({ title, subtitle }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-600 mt-1">{subtitle}</p>
    </div>
    <div className="bg-white rounded-lg shadow p-12 text-center">
      <div className="text-6xl mb-4 opacity-30">⚙️</div>
      <h3 className="text-2xl font-semibold text-gray-900 mb-2">Coming Soon</h3>
      <p className="text-gray-600">This section will be populated with data from Cloudflare Workers.</p>
    </div>
  </div>
);

// ============================================================================
// CONTROLLER (Application Logic)
// ============================================================================

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user] = useState({ name: 'John Demo', role: 'Administrator' });
  const [dashboardData, setDashboardData] = useState(null);
  const [modules, setModules] = useState([]);
  const [devOpsData, setDevOpsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (currentPage === 'dashboard') {
        const data = await DashboardModel.getStats();
        setDashboardData(data);
      } else if (currentPage === 'modules') {
        const data = await ModulesModel.getModules();
        setModules(data);
        const devops = await ModulesModel.getDevOpsPipeline();
        setDevOpsData(devops);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-4xl mb-4">🛰️</div>
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      );
    }

    switch (currentPage) {
      case 'dashboard':
        return dashboardData ? <DashboardView data={dashboardData} /> : null;
      case 'modules':
        return modules.length > 0 ? <ModulesView modules={modules} /> : null;
      case 'devops':
        return devOpsData ? <DevOpsPipelineView data={devOpsData} /> : null;
      case 'tasks':
        return <PlaceholderView title="Task Management" subtitle="View, manage, and track all tasks across your projects." />;
      case 'reports':
        return <PlaceholderView title="Reports & Analytics" subtitle="Generate, view, and export comprehensive system reports." />;
      case 'audit':
        return <PlaceholderView title="Audit Trail" subtitle="Comprehensive system-wide activity logging and monitoring." />;
      case 'settings':
        return <PlaceholderView title="Settings" subtitle="Manage your account, system preferences, and configurations." />;
      default:
        return <DashboardView data={dashboardData} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onMenuClick={() => setMenuOpen(true)} />
      <Navigation 
        currentPage={currentPage} 
        onNavigate={setCurrentPage} 
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
      />
      <main>
        {renderPage()}
      </main>
      <footer className="text-center py-8 text-gray-600 text-sm">
        ORBIT System v1.0 | React + Cloudflare Pages/Workers MVC Architecture
      </footer>
    </div>
  );
};

export default App;